import ToastConfig from '@config/toast/toast.config';
import { ThemeProvider } from '@mui/material';
import routerScheme from '@router';
import { createRoot } from 'react-dom/client';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import { LicenseInfo } from '@mui/x-license';

import 'react-toastify/dist/ReactToastify.css';
import './index.css';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
// import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import translationEN from './locales/translationEN.json';
import { theme } from '@store';
import * as Sentry from '@sentry/react';

const sentryRelease =
  import.meta.env.VITE_SENTRY_RELEASE ||
  import.meta.env.VITE_GIT_SHA ||
  (import.meta.env.MODE === 'development' ? 'dev' : 'prod');

const environment = import.meta.env.VITE_APP_ENV;

Sentry.init({
  // debug: true,
  environment,
  release: sentryRelease,
  dsn: import.meta.env.VITE_SENTRY_DSN,
  sendDefaultPii: false,
  integrations: [
    // Sentry.browserTracingIntegration(),
    // Sentry.replayIntegration(),
    // Sentry.feedbackIntegration({
    //   // Additional SDK configuration goes in here, for example:
    //   colorScheme: 'system',
    // }),
  ],
  // Set tracesSampleRate to 1.0 to capture 100%
  // of transactions for tracing.
  // Learn more at
  // https://docs.sentry.io/platforms/javascript/configuration/options/#traces-sample-rate
  tracesSampleRate: 0,
  // Set `tracePropagationTargets` to control for which URLs trace propagation should be enabled
  // tracePropagationTargets: [/^\//, /^https:\/\/yourserver\.io\/api/],
  // Capture Replay for 10% of all sessions,
  // plus for 100% of sessions with an error
  // Learn more at
  // https://docs.sentry.io/platforms/javascript/session-replay/configuration/#general-integration-configuration
  replaysSessionSampleRate: 0,
  replaysOnErrorSampleRate: 0,
  beforeSend(event) {
    const exception = event.exception?.values?.[0];

    if (exception?.value) {
      exception.value = `${environment.toUpperCase()} ${exception.value}`;
    }

    return event;
  },
});

Sentry.setTag('repo', 'bridge-frontend');

const sentryCreateBrowserRouter =
  Sentry.wrapCreateBrowserRouterV7(createBrowserRouter);

const sentryRouter = sentryCreateBrowserRouter(routerScheme);

const resources = {
  en: { ...translationEN },
};

i18n.use(initReactI18next).init({
  resources,
  lng: 'en',
  fallbackLng: 'en',
  debug: true,
  interpolation: {
    escapeValue: false,
  },
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 60_000,
    },
    hydrate: {
      queries: {
        gcTime: 60_000,
      },
    },
  },
});

const muiLicenseKey = import.meta.env.VITE_MUI_PRO_LICENSE_KEY;

LicenseInfo.setLicenseKey(muiLicenseKey);

createRoot(document.getElementById('root')!).render(
  <Sentry.ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <ToastContainer {...ToastConfig} />
      <ThemeProvider theme={theme}>
        <RouterProvider router={sentryRouter} />
      </ThemeProvider>
      {/* <ReactQueryDevtools initialIsOpen={false} /> */}
    </QueryClientProvider>
  </Sentry.ErrorBoundary>,
);
